"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { EquationField } from "@/components/tools/chem-reactions/balanced-equation";
import {
  EQUATION_ERROR_STRINGS,
  type EquationErrorStrings,
  equationErrorMessage,
} from "@/components/tools/chem-reactions/equation-view";
import {
  ICE_RESULT_STRINGS,
  IceResults,
} from "@/components/tools/chem-reactions/ice-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type EquilibriumSpecies,
  extentFromKnown,
  isPhysical,
  quotient,
  solveExtent,
} from "@/lib/tool-kit/chem-reactions/equilibrium";
import { reactionFromEquation } from "@/lib/tool-kit/chem-reactions/reaction-input";
import {
  fewestSigFigs,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const ICE_TABLE_CALCULATOR_STRINGS = {
  ...ICE_RESULT_STRINGS,
  equationLabel: "Equilibrium reaction",
  equationHint:
    "Write it with ⇌, <=> or =. Coefficients you type are used as written, because K belongs to the equation as written; with none typed, it is balanced for you. Add (s) or (l) to a pure solid or liquid.",
  equationPlaceholder: "H2(g) + I2(g) <=> 2HI(g)",
  tryLabel: "Try an example",
  modeLabel: "What do you want to find?",
  modeSolve: "Equilibrium concentrations from K",
  modeKFromEquilibrium: "K from equilibrium concentrations",
  modeKFromOne: "K from the start and one equilibrium value",
  kLabel: "Equilibrium constant, K",
  kHint: "A number such as 50.0 or 1.8e-5. It has no units here.",
  initialLabel: "Initial concentration of {species} (M)",
  initialHint: "Leave blank for 0.",
  equilibriumLabel: "Equilibrium concentration of {species} (M)",
  knownLabel: "Species with a known equilibrium value",
  results: "Results",
  enterK: "Enter K to solve the table.",
  enterAll:
    "Enter an equilibrium concentration greater than zero for every species not marked (s) or (l).",
  enterKnown: "Enter the equilibrium concentration of the chosen species.",
  badNumber:
    "Enter concentrations as numbers of zero or more, and K as a number greater than zero.",
  noStart:
    "Every starting concentration is zero, so nothing can react. Enter at least one concentration.",
  notPhysical:
    "That equilibrium value cannot be reached from these starting concentrations: some species would go below zero.",
  cannotSolve: "No solution was found. Check K and the concentrations.",
  errors: EQUATION_ERROR_STRINGS,
  unbalanced:
    "The coefficients do not balance this equation. Fix them, or remove all coefficients and it will be balanced for you.",
  noSolutes:
    "Every species is marked (s) or (l). K needs at least one gas or dissolved species.",
  balancedNote: "Balanced for you as: {equation}",
} as const;

type Strings = typeof ICE_TABLE_CALCULATOR_STRINGS;
type Mode = "solve" | "kFromEquilibrium" | "kFromOne";

const EXAMPLES = [
  "H2(g) + I2(g) <=> 2HI(g)",
  "N2O4(g) <=> 2NO2(g)",
  "N2(g) + 3H2(g) <=> 2NH3(g)",
  "CH3CO2H <=> H+ + CH3CO2-",
  "CaCO3(s) <=> CaO(s) + CO2(g)",
] as const;
const DEFAULT_SIG = 3;

type Values = Record<string, string>;

const DEFAULT_VALUES: Values = {
  i0: "1.00",
  i1: "1.00",
  e0: "0.220",
  e1: "0.220",
  e2: "1.56",
};

function reactionText(species: readonly EquilibriumSpecies[]): string {
  const side = (which: "reactant" | "product") =>
    species
      .filter((item) => item.side === which)
      .map((item) =>
        item.coefficient === 1
          ? item.label
          : `${item.coefficient} ${item.label}`
      )
      .join(" + ");
  return `${side("reactant")} ⇌ ${side("product")}`;
}

function readValue(
  raw: string | undefined,
  allowBlank: boolean
): number | null {
  if (raw === undefined || raw.trim() === "") {
    return allowBlank ? 0 : null;
  }
  const value = parseMeasuredNumber(raw);
  return value !== null && value >= 0 ? value : null;
}

function readAll(
  species: readonly EquilibriumSpecies[],
  values: Values,
  prefix: "i" | "e",
  allowBlank: boolean
): { numbers: number[]; raws: string[] } | null {
  const numbers: number[] = [];
  const raws: string[] = [];
  for (const [index, item] of species.entries()) {
    if (item.solid) {
      numbers.push(0);
      continue;
    }
    const raw = values[`${prefix}${index}`];
    const value = readValue(raw, allowBlank);
    if (value === null || (!allowBlank && value === 0)) {
      return null;
    }
    numbers.push(value);
    if (raw && raw.trim() !== "") {
      raws.push(raw);
    }
  }
  return { numbers, raws };
}

function Compute({
  species,
  mode,
  kText,
  values,
  known,
  strings,
  fmt,
}: {
  species: readonly EquilibriumSpecies[];
  mode: Mode;
  kText: string;
  values: Values;
  known: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (mode === "kFromEquilibrium") {
    const eq = readAll(species, values, "e", false);
    if (eq === null) {
      return <Notice>{strings.enterAll}</Notice>;
    }
    const k = quotient(species, eq.numbers);
    if (!(k > 0 && Number.isFinite(k))) {
      return <ErrorNotice>{strings.badNumber}</ErrorNotice>;
    }
    return (
      <IceResults
        equilibrium={eq.numbers}
        fmt={fmt}
        givenK={false}
        initial={eq.numbers}
        k={k}
        sig={fewestSigFigs(eq.raws, DEFAULT_SIG)}
        species={species}
        startKnown={false}
        strings={strings}
        x={0}
      />
    );
  }
  const start = readAll(species, values, "i", true);
  if (start === null) {
    return <ErrorNotice>{strings.badNumber}</ErrorNotice>;
  }
  if (mode === "solve") {
    return (
      <SolveResult
        fmt={fmt}
        kText={kText}
        raws={start.raws}
        species={species}
        start={start.numbers}
        strings={strings}
      />
    );
  }
  const knownRaw = values[`e${known}`];
  const knownValue = readValue(knownRaw, false);
  if (knownRaw === undefined || knownRaw.trim() === "") {
    return <Notice>{strings.enterKnown}</Notice>;
  }
  if (knownValue === null) {
    return <ErrorNotice>{strings.badNumber}</ErrorNotice>;
  }
  const x = extentFromKnown(species, start.numbers, known, knownValue);
  if (!isPhysical(species, start.numbers, x)) {
    return <ErrorNotice>{strings.notPhysical}</ErrorNotice>;
  }
  const eq = species.map((item, index) =>
    item.solid
      ? 0
      : Math.max(
          start.numbers[index] +
            (item.side === "reactant" ? -1 : 1) * item.coefficient * x,
          0
        )
  );
  const k = quotient(species, eq);
  if (!(k > 0 && Number.isFinite(k))) {
    return <ErrorNotice>{strings.badNumber}</ErrorNotice>;
  }
  return (
    <IceResults
      equilibrium={eq}
      fmt={fmt}
      givenK={false}
      initial={start.numbers}
      k={k}
      sig={fewestSigFigs([...start.raws, knownRaw], DEFAULT_SIG)}
      species={species}
      strings={strings}
      x={x}
    />
  );
}

function SolveResult({
  species,
  start,
  raws,
  kText,
  strings,
  fmt,
}: {
  species: readonly EquilibriumSpecies[];
  start: number[];
  raws: string[];
  kText: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (kText.trim() === "") {
    return <Notice>{strings.enterK}</Notice>;
  }
  const k = parseMeasuredNumber(kText);
  if (k === null || !(k > 0)) {
    return <ErrorNotice>{strings.badNumber}</ErrorNotice>;
  }
  const result = solveExtent(species, start, k);
  if (!result.ok) {
    return (
      <ErrorNotice>
        {result.reason === "noStart" ? strings.noStart : strings.cannotSolve}
      </ErrorNotice>
    );
  }
  return (
    <IceResults
      equilibrium={result.concentrations}
      fmt={fmt}
      givenK
      initial={start}
      k={k}
      sig={fewestSigFigs([...raws, kText], DEFAULT_SIG)}
      species={species}
      strings={strings}
      x={result.x}
    />
  );
}

export default function IceTableCalculatorWidget() {
  const strings = useToolUi(ICE_TABLE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [equation, setEquation] = useState<string>(EXAMPLES[0]);
  const [mode, setMode] = useState<Mode>("solve");
  const [kText, setKText] = useState("50.0");
  const [values, setValues] = useState<Values>(DEFAULT_VALUES);
  const [known, setKnown] = useState("2");
  const read = reactionFromEquation(equation);

  const set = (key: string, value: string) =>
    setValues({ ...values, [key]: value });
  const species = read.ok ? read.equilibrium : [];
  const live = species
    .map((item, index) => ({ item, index }))
    .filter((entry) => !entry.item.solid);
  const knownIndex = live.some((entry) => String(entry.index) === known)
    ? Number(known)
    : (live[0]?.index ?? 0);

  function problem() {
    if (read.ok) {
      return null;
    }
    if (read.reason === "parse") {
      return read.error.code === "empty" ? (
        <Notice>{strings.errors.empty}</Notice>
      ) : (
        <ErrorNotice>
          {equationErrorMessage(
            strings.errors as EquationErrorStrings,
            read.error
          )}
        </ErrorNotice>
      );
    }
    return (
      <ErrorNotice>
        {read.reason === "noSolutes" ? strings.noSolutes : strings.unbalanced}
      </ErrorNotice>
    );
  }

  const showInitial = mode !== "kFromEquilibrium";
  const showEquilibrium = mode === "kFromEquilibrium";

  return (
    <>
      <EquationField
        examples={EXAMPLES}
        id="ice-equation"
        onChange={setEquation}
        strings={strings}
        value={equation}
      />
      <PillGroup
        label={strings.modeLabel}
        name="ice-mode"
        onChange={setMode}
        options={[
          { value: "solve", label: strings.modeSolve },
          { value: "kFromEquilibrium", label: strings.modeKFromEquilibrium },
          { value: "kFromOne", label: strings.modeKFromOne },
        ]}
        value={mode}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {problem()}
        {read.ok ? (
          <>
            {read.balancedForYou ? (
              <Notice>
                {t(strings.balancedNote, {
                  equation: reactionText(read.equilibrium),
                })}
              </Notice>
            ) : null}
            {mode === "solve" ? (
              <TextField
                hint={strings.kHint}
                id="ice-k"
                label={strings.kLabel}
                numeric
                onChange={setKText}
                value={kText}
              />
            ) : null}
            <div className="grid gap-4 sm:grid-cols-3">
              {live.map(({ item, index }) => (
                <div className="space-y-4" key={`${index}-${item.label}`}>
                  {showInitial ? (
                    <TextField
                      hint={strings.initialHint}
                      id={`ice-i-${index}`}
                      label={t(strings.initialLabel, { species: item.label })}
                      numeric
                      onChange={(value) => set(`i${index}`, value)}
                      value={values[`i${index}`] ?? ""}
                    />
                  ) : null}
                  {showEquilibrium ||
                  (mode === "kFromOne" && index === knownIndex) ? (
                    <TextField
                      id={`ice-e-${index}`}
                      label={t(strings.equilibriumLabel, {
                        species: item.label,
                      })}
                      numeric
                      onChange={(value) => set(`e${index}`, value)}
                      value={values[`e${index}`] ?? ""}
                    />
                  ) : null}
                </div>
              ))}
            </div>
            {mode === "kFromOne" ? (
              <SelectField
                id="ice-known"
                label={strings.knownLabel}
                onChange={setKnown}
                options={live.map(({ item, index }) => ({
                  value: String(index),
                  label: item.label,
                }))}
                value={String(knownIndex)}
              />
            ) : null}
            <Compute
              fmt={fmt}
              known={knownIndex}
              kText={kText}
              mode={mode}
              species={species}
              strings={strings}
              values={values}
            />
          </>
        ) : null}
      </section>
    </>
  );
}
